/**
 * File: accountingRecord.ts
 * Path: packages/config/src/accounting/accountingRecord.ts
 * Description: Where the books are stored and how a stored document is read
 * back safely.
 *
 *   accounting/{profileId}                    the profile (BooksProfile)
 *   accounting/{profileId}/accounts/{id}      its chart of accounts
 *   accounting/{profileId}/journals/{id}      posted journals, never edited
 *   accounting/{profileId}/events/{id}        the change log
 *   accounting/{profileId}/taxYears/{yearEnd} what only Liam knows about a tax year (taxYear.ts)
 *   accounting/{profileId}/filings/{obligationId} filed or paid, and a changed due date (taxCalendar.ts)
 *   accounting/{profileId}/zohoChecks/{monthEnd} a month-end Compare with Zoho result (monthEndCheck.ts)
 *
 * Only the `accounting` callable writes here (firestore.rules). The company
 * books are "company"; a person's own books are "personal-<their uid>" and
 * only they can read them.
 */

import type {
  AccountRole,
  AccountType,
  BooksEvent,
  BooksEventAction,
  BooksProfile,
  Journal,
  JournalDocument,
  JournalLine,
  JournalSource,
  LedgerAccount,
  ProfileKind,
} from "./accountingTypes";
import { financialYearStart, isBooksDate } from "./ledger";

export const ACCOUNTING_COLLECTIONS = {
  profiles: "accounting",
  accounts: "accounts",
  journals: "journals",
  events: "events",
} as const;

/** The one callable that writes the books (apps/server/src/features/accounting). */
export const ACCOUNTING_FUNCTION = "accounting";

export const COMPANY_PROFILE_ID = "company";
export const personalProfileId = (uid: string) => `personal-${uid}`;

/**
 * Personal books are Liam's alone: only this login can open or set them up,
 * whoever else holds finance. Other admins see the company's books only.
 */
export const BOOKS_OWNER_EMAIL = "liam@idksolutions.co.za";
export const isBooksOwner = (email: string | null | undefined): boolean =>
  (email ?? "").trim().toLowerCase() === BOOKS_OWNER_EMAIL;

/** South African companies and the tax year both most often end in February. */
export const DEFAULT_YEAR_END_MONTH = 2;

/** Today in South Africa as YYYY-MM-DD: the books' idea of "today". */
export const booksToday = (now: Date = new Date()): string =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "Africa/Johannesburg" }).format(now);

/** The first day of the financial year `today` is in: where new books start. */
export const defaultBooksStart = (today: string, yearEndMonth = DEFAULT_YEAR_END_MONTH) =>
  financialYearStart(today, yearEndMonth);

export const PROFILE_KIND_LABELS: Record<ProfileKind, string> = {
  company: "IDK Solutions (Pty) Ltd",
  personal: "My personal books",
};

/** Only this many events are shown; the log itself is never trimmed. */
export const BOOKS_EVENTS_LIMIT = 200;

type Raw = Record<string, unknown>;

const str = (v: unknown, fallback = ""): string => (typeof v === "string" ? v : fallback);
const int = (v: unknown): number =>
  typeof v === "number" && Number.isSafeInteger(v) && v >= 0 ? v : 0;
const millis = (v: unknown): number | null => {
  if (v && typeof (v as { toMillis?: () => number }).toMillis === "function") {
    return (v as { toMillis: () => number }).toMillis();
  }
  return typeof v === "number" ? v : null;
};

const ACCOUNT_TYPES: readonly AccountType[] = ["asset", "liability", "equity", "income", "expense"];
const ROLES: readonly AccountRole[] = ["retainedEarnings", "suspense", "directorLoan", "loanToCompany", "debtors", "clientCredit"];
const SOURCES: readonly JournalSource[] = ["manual", "opening", "reversal", "import"];
const ACTIONS: readonly BooksEventAction[] = [
  "profileCreated",
  "accountAdded",
  "accountChanged",
  "journalPosted",
  "journalReversed",
  "openingImported",
  "periodLocked",
  "periodUnlocked",
  "booksStartMoved",
  "documentAttached",
  "taxYearSaved",
  "taxRecordsSaved",
  "provisionalPaid",
  "provisionalUndone",
  "filingSaved",
  "companyDetailsSaved",
  "beneficialOwnershipSaved",
  "historyImported",
  "historyLinked",
  "bankImported",
  "bankImportUndone",
  "bankReconciled",
  "bankReconciliationUndone",
  "bankRuleSaved",
  "invoiceIssued",
  "invoiceVoided",
  "creditNoteIssued",
  "paymentReceived",
  "creditApplied",
  "zohoCheckSaved",
  "journalRegrouped",
  "budgetSaved",
  "recurringPaymentSaved",
  "recurringPaymentPaid",
];

export const isAccountType = (v: unknown): v is AccountType =>
  ACCOUNT_TYPES.includes(v as AccountType);

export const toBooksProfile = (id: string, data: Raw | undefined | null): BooksProfile | null => {
  const r = data ?? {};
  const kind = r.kind === "company" || r.kind === "personal" ? r.kind : null;
  if (!kind || !isBooksDate(r.booksStart)) return null;
  const yearEndMonth = int(r.yearEndMonth);
  return {
    id,
    kind,
    name: str(r.name, PROFILE_KIND_LABELS[kind]),
    ownerUid: typeof r.ownerUid === "string" ? r.ownerUid : null,
    booksStart: r.booksStart,
    yearEndMonth: yearEndMonth >= 1 && yearEndMonth <= 12 ? yearEndMonth : DEFAULT_YEAR_END_MONTH,
    lockedThrough: isBooksDate(r.lockedThrough) ? r.lockedThrough : null,
    journalCount: int(r.journalCount),
    createdAt: millis(r.createdAt),
  };
};

export const toLedgerAccount = (id: string, data: Raw | undefined | null): LedgerAccount | null => {
  const r = data ?? {};
  if (!isAccountType(r.type)) return null;
  return {
    id,
    code: str(r.code),
    name: str(r.name),
    type: r.type,
    taxLine: typeof r.taxLine === "string" && r.taxLine !== "" ? r.taxLine : null,
    role: ROLES.includes(r.role as AccountRole) ? (r.role as AccountRole) : null,
    money: r.money === true,
    archived: r.archived === true,
  };
};

const toLine = (v: unknown): JournalLine => {
  const r = (v ?? {}) as Raw;
  return {
    accountId: str(r.accountId),
    debitCents: int(r.debitCents),
    creditCents: int(r.creditCents),
    memo: str(r.memo),
    ...(typeof r.invoice === "string" && r.invoice !== "" ? { invoice: r.invoice } : {}),
  };
};

/** Where a profile's receipts live in Cloud Storage (storage.rules). */
export const receiptFolder = (profileId: string) => `${ACCOUNTING_COLLECTIONS.profiles}/${profileId}/receipts`;

const toDocument = (v: unknown): JournalDocument[] => {
  if (!v || typeof v !== "object") return [];
  const d = v as Record<string, unknown>;
  if (typeof d.path !== "string" || d.path === "") return [];
  return [
    {
      path: d.path,
      name: typeof d.name === "string" ? d.name : "",
      contentType: typeof d.contentType === "string" ? d.contentType : "",
      size: typeof d.size === "number" ? d.size : 0,
    },
  ];
};

export const toJournal = (id: string, data: Raw | undefined | null): Journal | null => {
  const r = data ?? {};
  if (!isBooksDate(r.date) || !Array.isArray(r.lines)) return null;
  return {
    id,
    number: int(r.number),
    date: r.date,
    narration: str(r.narration),
    lines: r.lines.map(toLine),
    totalCents: int(r.totalCents),
    source: SOURCES.includes(r.source as JournalSource) ? (r.source as JournalSource) : "manual",
    reversalOf: typeof r.reversalOf === "string" ? r.reversalOf : null,
    reversedBy: typeof r.reversedBy === "string" ? r.reversedBy : null,
    documents: Array.isArray(r.documents) ? r.documents.flatMap(toDocument) : [],
    externalRef: typeof r.externalRef === "string" && r.externalRef !== "" ? r.externalRef : null,
    clientId: typeof r.clientId === "string" && r.clientId !== "" ? r.clientId : null,
    clientNumber: typeof r.clientNumber === "number" && Number.isSafeInteger(r.clientNumber) ? r.clientNumber : null,
    jobId: typeof r.jobId === "string" && r.jobId !== "" ? r.jobId : null,
    party: typeof r.party === "string" && r.party !== "" ? r.party : null,
    project: typeof r.project === "string" && r.project !== "" ? r.project : null,
    ...(isBooksDate(r.dueOn) ? { dueOn: r.dueOn } : {}),
    createdBy: str(r.createdBy),
    createdAt: millis(r.createdAt),
  };
};

export const toBooksEvent = (id: string, data: Raw | undefined | null): BooksEvent | null => {
  const r = data ?? {};
  if (!ACTIONS.includes(r.action as BooksEventAction)) return null;
  return {
    id,
    action: r.action as BooksEventAction,
    summary: str(r.summary),
    journalId: typeof r.journalId === "string" ? r.journalId : null,
    accountId: typeof r.accountId === "string" ? r.accountId : null,
    byUid: str(r.byUid),
    byEmail: str(r.byEmail),
    at: millis(r.at),
  };
};

/** Journals in posting order: by date, then by number. */
export const byJournalOrder = (a: Journal, b: Journal) =>
  a.date.localeCompare(b.date) || a.number - b.number;
